import { Radio } from 'antd'
import { reaction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'

import { Form, FormField } from '@/Core/UI/Form'
import { FieldCell, FieldGrid } from '@/Ship/Components/EntityCard'

import { OWNER_FIELDS } from '../Contracts/Activity.types'
import { ACTIVITY_FIELD_LABELS as L } from '../Contracts/ActivityFieldLabels'
import { ownerKindOf, relationUuidOf } from '../Helpers/ownerContact'

import type { ActivityFormContextLike, ActivityOwnerKind } from '../Contracts/Activity.types'

interface Props {
    formContext: ActivityFormContextLike
}

const OWNER_OPTIONS: Array<{ value: ActivityOwnerKind; label: string }> = [
    { value: 'client', label: L.client },
    { value: 'carrier', label: L.carrier },
]

/**
 * Зміна власника скидає контакт (`SPEC-011` §8.4, К-9, П25): контакт іншого
 * клієнта лишатися обраним не може — бек його однаково відхилить (І5).
 *
 * Стежимо за uuid, а не за об'єктом: картка після «Зберегти» кладе в форму
 * нову копію того самого власника, і контакт від цього зникати не має. Перша
 * поява власника (дані картки прийшли вже після монтування) — теж не зміна.
 */
const useResetContactOnOwnerChange = (formContext: ActivityFormContextLike): void => {
    useEffect(() => {
        const disposers = (Object.keys(OWNER_FIELDS) as ActivityOwnerKind[]).map((kind) => {
            const { owner, contact } = OWNER_FIELDS[kind]

            return reaction(
                () => relationUuidOf(formContext.values?.[owner]),
                (next, previous) => {
                    if (previous === null || next === previous) return
                    if (formContext.values?.[contact] != null) formContext.setValue?.(contact, null)
                }
            )
        })

        return () => disposers.forEach((dispose) => dispose())
    }, [formContext])
}

/**
 * Блок «Контрагент» (`SPEC-011` §8.4): перемикач «Клієнт» / «Перевізник»,
 * власник і його контакт.
 *
 *  - **перемикач** — лише в редагуванні й не тоді, коли справу заводять із
 *    картки власника (`ownerFixed`, В8). Перемикання очищає іншу пару
 *    (К-6): справа належить рівно одному власнику (І4). `null`, а не
 *    видалення ключа — щоб PATCH явно зняв попереднього власника;
 *  - **власник** — селект у редагуванні, посилання на картку в перегляді
 *    (`view` поля в ресурсі);
 *  - **контакт** — коли власника обрано, і в редагуванні, і в перегляді (як
 *    у легасі); пропонуються лише контакти обраного власника
 *    (`filterContactsOfOwner` у ресурсі).
 *
 * Окремий observer: шаблон картки значень полів не читає (`SPEC-002` §4.2),
 * а цей блок перемальовується лише зі зміною власника.
 */
export const ActivityOwnerFields = observer(({ formContext }: Props) => {
    useResetContactOnOwnerChange(formContext)

    const values = formContext.values ?? {}
    const kind = ownerKindOf(values)
    const { owner, contact } = OWNER_FIELDS[kind]
    const isEditable = formContext.isEditable !== false
    const hasOwner = relationUuidOf(values[owner]) !== null
    const canSwitch = isEditable && values.ownerFixed !== true

    const switchTo = (next: ActivityOwnerKind): void => {
        if (next === kind) return

        const other = OWNER_FIELDS[kind]

        formContext.setValue?.('ownerType', next)
        formContext.setValue?.(other.owner, null)
        formContext.setValue?.(other.contact, null)
    }

    return (
        <>
            {canSwitch ? (
                <Radio.Group
                    block
                    size="large"
                    optionType="button"
                    buttonStyle="solid"
                    value={kind}
                    options={OWNER_OPTIONS}
                    onChange={(event) => switchTo(event.target.value as ActivityOwnerKind)}
                    style={{ marginBottom: 16 }}
                    aria-label="Чия справа"
                />
            ) : null}
            <Form formContext={formContext}>
                <FieldGrid>
                    <FieldCell key={owner}>
                        <FormField name={owner} />
                    </FieldCell>
                    {hasOwner ? (
                        <FieldCell key={contact}>
                            <FormField name={contact} />
                        </FieldCell>
                    ) : null}
                </FieldGrid>
            </Form>
        </>
    )
})
